<template>
    <table class="custom-table">
        <thead>
            <tr>
                <th v-for="(header, hIndex) in headers" :key="hIndex" @mouseenter="hoveredColumn = header.value" @mouseleave="hoveredColumn = null" @click="header.sortable && sortColumn(header)">
                    <div :class="{sortable: header.sortable}">
                        <slot :name="`header.${header.value}`" :header="header">
                            {{ header.text }}
                        </slot>

                        <!-- Show icon if sorted or hovered -->
                        <v-icon v-if="header.sortable" :class="{'sorting-icon': true, 'sorting-icon-active': sortBy === header.value || hoveredColumn === header.value}" size="16">
                            {{(sortOrder === 'desc' ? 'mdi-arrow-down' : 'mdi-arrow-up')}}
                        </v-icon>
                    </div>
                </th>
            </tr>
        </thead>

        <tbody>
            <tr v-for="(item, rowIndex) in sortedItems" :key="item[itemKey] || rowIndex">
                <slot name="row" :item="item" :index="rowIndex">
                    <td v-for="(header, colIndex) in headers" :key="colIndex">
                        <slot :name="`item.${header.value}`" :item="item" :value="item[header.value]">
                            {{ item[header.value] }}
                        </slot>
                    </td>
                </slot>
            </tr>
        </tbody>
    </table>
</template>

<script>
import { isInvalidValue } from '../../utils'

export default {
  name: 'CustomTable',

  props: {
    headers: {
      type: Array,
      required: true
    },
    items: {
      type: Array,
      required: true
    },
    itemKey: {
      type: String,
      default: 'id'
    }
  },

  data () {
    return {
      sortBy: null,
      sortOrder: null,
      hoveredColumn: null,
      columnTypes: {}
    }
  },

  watch: {
    items: {
      handler () {
        this.detectColumnTypes()
      },
      immediate: true
    }
  },

  computed: {
    sortedItems () {
      if (!this.sortBy || !this.sortOrder) return this.items

      const type = this.columnTypes[this.sortBy]

      return [...this.items].sort((a, b) => {
        const aVal = a[this.sortBy]
        const bVal = b[this.sortBy]

        const aInvalid = isInvalidValue(aVal)
        const bInvalid = isInvalidValue(bVal)

        if (aInvalid && !bInvalid) return 1
        if (!aInvalid && bInvalid) return -1
        if (aInvalid && bInvalid) return 0

        const valA = this.sortValue(aVal, type)
        const valB = this.sortValue(bVal, type)

        if (valA < valB) return this.sortOrder === 'asc' ? -1 : 1
        if (valA > valB) return this.sortOrder === 'asc' ? 1 : -1
        return 0
      })
    }
  },

  methods: {
    sortColumn (header) {
      if (this.sortBy !== header.value) {
        this.sortBy = header.value
        this.sortOrder = 'asc'
      } else {
        this.sortOrder =
          this.sortOrder === 'asc'
            ? 'desc'
            : this.sortOrder === 'desc'
              ? null
              : 'asc'

        if (!this.sortOrder) {
          this.sortBy = null
        }
      }
    },

    detectColumnTypes () {
      this.columnTypes = {}

      this.headers.forEach(header => {
        const key = header.value

        for (let i = 0; i < this.items.length; i++) {
          const value = this.items[i][key]
          if (value !== undefined && value !== null) {
            if (this.isDateValue(value)) {
              this.columnTypes[key] = 'date'
            } else if (!isNaN(value) && value !== '') {
              this.columnTypes[key] = 'number'
            } else {
              this.columnTypes[key] = 'string'
            }
            break
          }
        }

        if (!this.columnTypes[key]) {
          this.columnTypes[key] = 'string'
        }
      })
    },

    isDateValue (val) {
      if (!val) return false
      if (val instanceof Date && !isNaN(val)) return true

      if (typeof val === 'string') {
        const parsed = Date.parse(val)
        return !isNaN(parsed)
      }
      return false
    },

    sortValue (val, type) {
      if (type === 'date') return new Date(val).getTime()
      if (type === 'number') return Number(val)
      return (val || '').toString().toLowerCase()
    }
  }
}
</script>
<style scoped>
.custom-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
}

thead {
  background-color: #f9fafb;
}

th {
  text-align: left;
  padding: 8px 12px;
  font-size: 16px;
  font-weight: 600;
  border-bottom: 1px solid #e5e7eb;
}

th>div.sortable {
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
  gap:10px;
}

tbody tr {
  transition: background-color 0.2s ease;
}

tbody tr:hover {
  background-color: #f9fafb;
}

td {
  padding: 8px 12px;
  font-size: 14px;
  border-bottom: 1px solid #e5e7eb;
}

tbody tr:last-child td {
  border-bottom: none;
}

.sorting-icon{
  transition: transform 0.2s;
  opacity: 0;
}
.sorting-icon-active{
  opacity: 1;
}
</style>
